// packages/web-ui/src/components/welcome/KeyboardCheatSheet.tsx
//
// W2-S10 Task 3 (AC #3) — modal cheat-sheet listing every binding from the
// `KEYMAP_GROUPS` registry. Triggered by `useKeymap()` (which owns the `?` /
// `Ctrl+/` listeners and the open/close state).
//
// Located under `components/welcome/` per the W2-S10 scope assignment — the
// folder hosts the new-user help-surface UI (welcome state + cheat sheet).
// Future polish may relocate to `components/a11y/`.

import type { JSX } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { KEYMAP_GROUPS } from '../../state/keymap.js';

export interface KeyboardCheatSheetProps {
  /** True when the modal is open. */
  isOpen: boolean;
  /** Close callback (Esc / backdrop click / close button). */
  onClose: () => void;
  /** Optional test-id override. */
  testId?: string;
}

export function KeyboardCheatSheet({
  isOpen,
  onClose,
  testId = 'keyboard-cheat-sheet',
}: KeyboardCheatSheetProps): JSX.Element | null {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Autofocus the dialog when opened so screen readers announce it.
  useEffect(() => {
    if (isOpen) {
      dialogRef.current?.focus();
    }
  }, [isOpen]);

  // Focus-trap: Tab stays inside the modal.
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const root = dialogRef.current;
      if (root === null) return;
      const focusable = root.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${testId}-title`}
      data-testid={testId}
      onKeyDown={onKeyDown}
      onClick={(e) => {
        // Backdrop click closes; clicks inside the panel do NOT bubble here.
        if (e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-[2100] flex items-center justify-center bg-black/60 p-4"
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        data-testid={`${testId}-panel`}
        className="bg-cream-50 border-taupe-500 flex max-h-[85vh] w-full max-w-2xl flex-col gap-5 overflow-y-auto rounded-none border p-7 outline-none"
      >
        <header className="flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <span className="text-audi-red text-[11px] font-semibold uppercase tracking-[0.22em]">
              Reference
            </span>
            <h2 id={`${testId}-title`} className="text-fg text-xl font-extrabold tracking-[-0.01em]">
              Keyboard shortcuts
            </h2>
          </div>
          <button
            type="button"
            data-testid={`${testId}-close`}
            onClick={onClose}
            aria-label="Close keyboard shortcuts"
            className="border-taupe-500 hover:border-taupe-900 hover:bg-cream-100 rounded-none border px-3 py-1 text-[11px] uppercase tracking-wide"
          >
            Close
          </button>
        </header>

        <div
          data-testid={`${testId}-groups`}
          className="grid grid-cols-1 gap-4 md:grid-cols-2"
        >
          {KEYMAP_GROUPS.map((group) => (
            <section
              key={group.id}
              data-testid={`${testId}-group-${group.id}`}
              aria-labelledby={`${testId}-group-${group.id}-title`}
              className="flex flex-col gap-2"
            >
              <h3
                id={`${testId}-group-${group.id}-title`}
                className="text-xs font-semibold uppercase tracking-wide text-taupe-700"
              >
                {group.title}
              </h3>
              <ul role="list" className="flex flex-col gap-1">
                {group.bindings.map((b, i) => (
                  <li
                    key={`${group.id}-${i}`}
                    data-testid={`${testId}-binding-${group.id}-${i}`}
                    className="flex items-center justify-between gap-2 text-xs"
                  >
                    <kbd className="rounded border border-taupe-300 bg-cream-50 px-1.5 py-0.5 font-mono text-[11px] text-taupe-900">
                      {b.keys}
                    </kbd>
                    <span className="flex-1 text-right text-taupe-900">
                      {b.description}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
